import { TourProvider } from '@reactour/tour';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactFlowProvider } from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { debounce } from "lodash";
import { useCallback, useEffect } from "react";

import { BrowserRouter as Router, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import "./App.css";
import ErrorBoundary from "./components/ErrorBoundary";
import { AdminProvider } from "./contexts/AdminContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ChartProvider } from "./contexts/ChartContext";
import { DashboardProvider } from "./contexts/DashboardContext";
import { NotificationsProvider } from "./contexts/NotificationsContext";

import { QuizProvider } from "./contexts/QuizContext";

import { SocketProvider } from "./contexts/SocketContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { UserProvider } from "./contexts/UserContext";
import AppRoutes from "./routes/AppRoutes";


const AppContent = () => {
  const { isAuthenticated, user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();

  // Fonction fetchData pour synchroniser les données
  const fetchAppData = useCallback(
  debounce(async () => {
    // Sécurité de base : on ne fait rien si pas authentifié
    if (!isAuthenticated || !user) {
      return;
    }

    try {
      // ────────────────────────────────────────────────────────────────
      // ICI les vrais chargements dont l'app a besoin après login/montage
      // Exemples selon architecture actuelle (adapte les noms) :

      await Promise.all([
        // useParcoursStore.getState().fetchParcours(),           // zustand exemple
        // progressionStore.fetchGlobalProgression(),
        // fetchNotifications(),
        // fetchRecentActivity(),
        // etc.
      ]);

      // Option : un flag global si besoin
      // setAppDataLoaded(true);

      // ────────────────────────────────────────────────────────────────
    } catch (error) {
      console.error("Erreur lors du chargement des données app :", error);

      // Plus besoin de gestion manuelle 401/403 → l'intercepteur s'en charge
      // → ici on gère uniquement les erreurs "métier" non bloquantes

      // Exemple de toast soft pour l'utilisateur
      if (error.response?.status >= 500) {
        toast.warning("Certaines données n'ont pas pu être chargées (erreur serveur)");
      } else if (!error.response) {
        toast.warning("Problème de connexion – certaines fonctionnalités peuvent être limitées");
      }
    }
  }, 900), // debounce raisonnable – 800–1200 ms souvent un bon compromis

  [
    isAuthenticated,
    user,
    // ici toutes les fonctions/stores nécessaire
    // Exemple :
    // parcoursStore.fetchParcours,
    // progressionStore.fetchGlobalProgression,
  ]
);

  useEffect(() => {
    if (!authLoading && isAuthenticated && user) {
      fetchAppData();
    }
  }, [authLoading, isAuthenticated, user, fetchAppData]);

  return (
    <>
      <AppRoutes />
      
    </>
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, 
      retry: 1, 
    },
  },
});

function App() {
  return (
    <Router>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <UserProvider>
              <AdminProvider>                            
                
                  <SocketProvider>
                    <NotificationsProvider>
                      <DashboardProvider>
                        <ReactFlowProvider>
                          <TourProvider>
                            
                                                             
                                  <ErrorBoundary>
                                    <ChartProvider>
                                      
                                        <QuizProvider>
                                            <AppContent />
                                          </QuizProvider>
                                        
                                      </ChartProvider>
                                    </ErrorBoundary>
                                  <ToastContainer position="bottom-right" />                                
                              
                            
                          </TourProvider>
                        </ReactFlowProvider>
                      </DashboardProvider>
                    </NotificationsProvider>
                  </SocketProvider>
                
              </AdminProvider>
            </UserProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </Router>
  );
}

export default App;
